import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from 'recharts'
import api from '@/lib/api'
import { formatPercent, formatNumber } from '@/lib/utils'
import { LoadingSpinner } from '@/components/ui'
import { AlertTriangle } from 'lucide-react'

interface Fund {
  fund_id: number
  fund_name: string
  weight: number
  has_model: boolean
  alpha: number | null
  factors: Record<string, number>
}

interface AttributionData {
  funds: Fund[]
  portfolio_exposures: Record<string, number>
  portfolio_alpha: number
  all_factors: string[]
  coverage: number
}

export default function PortfolioFactors() {
  const [months, setMonths] = useState(36)

  const { data, isLoading, error } = useQuery<AttributionData>({
    queryKey: ['portfolio-attribution', months],
    queryFn: () =>
      api.get(`/factors/portfolio-attribution?months=${months}`).then((r) => r.data),
  })

  if (isLoading) return <LoadingSpinner />
  if (error) return <div className="text-destructive">Failed to load attribution data.</div>
  if (!data || data.funds.length === 0) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Portfolio Factor Attribution</h1>
        <p className="text-muted-foreground">No portfolio data available.</p>
      </div>
    )
  }

  const fundsWithModels = data.funds.filter((f) => f.has_model)
  const fundsWithoutModels = data.funds.filter((f) => !f.has_model)
  const coveragePct = data.coverage * 100

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Portfolio Factor Attribution</h1>
          <p className="text-muted-foreground">
            Factor exposures and alpha decomposition across the portfolio
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Coverage:</span>
            <span
              className={`text-sm font-semibold ${
                coveragePct >= 80 ? 'text-green-600' : coveragePct >= 50 ? 'text-yellow-600' : 'text-red-600'
              }`}
            >
              {coveragePct.toFixed(1)}%
            </span>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-muted-foreground">Period:</label>
            <select
              className="h-9 px-3 border border-input rounded-md bg-background text-sm"
              value={months}
              onChange={(e) => setMonths(parseInt(e.target.value))}
            >
              <option value={12}>1 Year</option>
              <option value={24}>2 Years</option>
              <option value={36}>3 Years</option>
              <option value={60}>5 Years</option>
              <option value={120}>10 Years</option>
            </select>
          </div>
        </div>
      </div>

      {/* Alpha Summary Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <p className="text-sm text-muted-foreground">Portfolio Alpha (monthly)</p>
          <p className={`text-2xl font-bold font-mono ${data.portfolio_alpha >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatPercent(data.portfolio_alpha)}
          </p>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <p className="text-sm text-muted-foreground">Portfolio Alpha (annualized)</p>
          <p className={`text-2xl font-bold font-mono ${data.portfolio_alpha >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatPercent(data.portfolio_alpha * 12)}
          </p>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <p className="text-sm text-muted-foreground">Funds with Models</p>
          <p className="text-2xl font-bold font-mono">
            {fundsWithModels.length} / {data.funds.length}
          </p>
        </div>
      </div>

      {/* Portfolio Exposures Summary - Horizontal Bar Chart */}
      {data.all_factors.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-4">Portfolio Factor Exposures</h2>
          <ResponsiveContainer width="100%" height={Math.max(300, data.all_factors.length * 40)}>
            <BarChart
              layout="vertical"
              data={data.all_factors.map((f) => ({
                factor: f,
                beta: data.portfolio_exposures[f] ?? 0,
              }))}
              margin={{ top: 5, right: 30, left: 150, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tickFormatter={(v) => formatNumber(v, 2)} />
              <YAxis type="category" dataKey="factor" width={140} tick={{ fontSize: 12 }} />
              <Tooltip formatter={(value: number) => formatNumber(value, 4)} />
              <ReferenceLine x={0} stroke="#666" />
              <Bar dataKey="beta" name="Beta">
                {data.all_factors.map((f) => (
                  <Cell
                    key={f}
                    fill={(data.portfolio_exposures[f] ?? 0) >= 0 ? '#16a34a' : '#dc2626'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Alpha Waterfall */}
      {fundsWithModels.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-4">Alpha Contribution (Annualized)</h2>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart
              data={[
                ...fundsWithModels.map((f) => ({
                  name: f.fund_name.length > 20 ? f.fund_name.slice(0, 18) + '...' : f.fund_name,
                  value: (f.alpha ?? 0) * f.weight * 12,
                  fullName: f.fund_name,
                })),
                {
                  name: 'Portfolio Total',
                  value: data.portfolio_alpha * 12,
                  fullName: 'Portfolio Total',
                },
              ]}
              margin={{ top: 5, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => formatPercent(v)} />
              <Tooltip
                formatter={(value: number) => formatPercent(value, 3)}
                labelFormatter={(label: string, payload: any[]) =>
                  payload?.[0]?.payload?.fullName ?? label
                }
              />
              <ReferenceLine y={0} stroke="#666" />
              <Bar dataKey="value" name="Alpha Contribution">
                {[
                  ...fundsWithModels.map((f) => (
                    <Cell
                      key={f.fund_id}
                      fill={((f.alpha ?? 0) * f.weight * 12) >= 0 ? '#16a34a' : '#dc2626'}
                    />
                  )),
                  <Cell
                    key="total"
                    fill={data.portfolio_alpha * 12 >= 0 ? '#2563eb' : '#dc2626'}
                  />,
                ]}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Factor Exposure Heatmap Table */}
      {data.all_factors.length > 0 && fundsWithModels.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-4 overflow-x-auto">
          <h2 className="text-lg font-semibold mb-4">Factor Exposure Heatmap</h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 px-3 font-medium text-muted-foreground">Factor</th>
                {fundsWithModels.map((f) => (
                  <th key={f.fund_id} className="text-center py-2 px-2 font-medium text-muted-foreground" title={f.fund_name}>
                    {f.fund_name.length > 15 ? f.fund_name.slice(0, 13) + '...' : f.fund_name}
                  </th>
                ))}
                <th className="text-center py-2 px-3 font-semibold">Portfolio</th>
              </tr>
            </thead>
            <tbody>
              {/* Weight row */}
              <tr className="border-b border-border bg-muted/50">
                <td className="py-2 px-3 font-medium text-muted-foreground italic">Weight</td>
                {fundsWithModels.map((f) => (
                  <td key={f.fund_id} className="text-center py-2 px-2 font-mono text-xs">
                    {formatPercent(f.weight)}
                  </td>
                ))}
                <td className="text-center py-2 px-3 font-mono text-xs font-semibold">
                  {formatPercent(data.coverage)}
                </td>
              </tr>
              {/* Factor rows */}
              {data.all_factors.map((factor) => (
                <tr key={factor} className="border-b border-border">
                  <td className="py-2 px-3 font-medium">{factor}</td>
                  {fundsWithModels.map((f) => {
                    const beta = f.factors[factor] ?? null
                    return (
                      <td
                        key={f.fund_id}
                        className="text-center py-2 px-2 font-mono text-xs"
                        style={beta !== null ? getCellStyle(beta) : {}}
                      >
                        {beta !== null ? formatNumber(beta, 3) : '-'}
                      </td>
                    )
                  })}
                  <td
                    className="text-center py-2 px-3 font-mono text-xs font-semibold"
                    style={getCellStyle(data.portfolio_exposures[factor] ?? 0)}
                  >
                    {formatNumber(data.portfolio_exposures[factor] ?? 0, 3)}
                  </td>
                </tr>
              ))}
              {/* Alpha row */}
              <tr className="border-t-2 border-border">
                <td className="py-2 px-3 font-semibold">Alpha (monthly)</td>
                {fundsWithModels.map((f) => (
                  <td
                    key={f.fund_id}
                    className={`text-center py-2 px-2 font-mono text-xs font-semibold ${
                      (f.alpha ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {f.alpha !== null ? formatPercent(f.alpha) : '-'}
                  </td>
                ))}
                <td
                  className={`text-center py-2 px-3 font-mono text-xs font-semibold ${
                    data.portfolio_alpha >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {formatPercent(data.portfolio_alpha)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Funds without models */}
      {fundsWithoutModels.length > 0 && (
        <div className="bg-card border border-yellow-500/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            <h2 className="text-lg font-semibold">Funds Without Factor Models</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            The following {fundsWithoutModels.length} fund(s) ({formatPercent(1 - data.coverage)} of portfolio weight) do not have saved factor models.
            Build models on the{' '}
            <Link to="/factors" className="text-primary underline hover:text-primary/80">
              Factor Modeling
            </Link>{' '}
            page.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {fundsWithoutModels.map((f) => (
              <div key={f.fund_id} className="flex items-center justify-between bg-muted/50 rounded px-3 py-2 text-sm">
                <span className="truncate mr-2">{f.fund_name}</span>
                <span className="font-mono text-muted-foreground whitespace-nowrap">
                  {formatPercent(f.weight)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/** Returns inline style for heatmap cell coloring based on beta value. */
function getCellStyle(value: number): React.CSSProperties {
  const maxIntensity = 0.35
  const clampedAbs = Math.min(Math.abs(value), 2) / 2 // normalize to [0,1] for betas up to 2
  const opacity = clampedAbs * maxIntensity
  const color = value >= 0 ? `rgba(22, 163, 74, ${opacity})` : `rgba(220, 38, 38, ${opacity})`
  return { backgroundColor: color }
}
